import { useFlag, useNewCode } from '@calendly/feature-check-react'
import { useQueryParams } from '@calendly/hooks'
import { createContext, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'

import { useMeetingFromCache, useSSEConnectedForPolling } from 'components/meetings'
import { useActiveMeeting, useLiveMeetingDetailPolling } from 'components/meetings/hooks'
import { defensiveMergeMeeting } from 'components/meetings/hooks/helpers'
import { isPendingEvent } from 'components/meetings/meeting_detail/utils'
import { useMeetingsRealtimeFeaturesEnabled } from 'features/meetings/hooks'
import { useRightRail } from 'features/right_rail'
import { useScope } from 'hooks/meetings/use_scope'
import { timezoneSelector } from 'selectors/current_user'
import { useLazyGQLGetMeetingQuery } from 'shared/api/meetings'

import { useLazyGetMeetingQuery } from '../api'
import {
  useGetMeetingQuery as useGQLGetMeetingQuery,
  useLazyGetMeetingQuery as useLazyGetMeetingFromSearchQuery
} from '../graphql_api'
import { useSelectedView } from '../hooks/use_selected_view'
import { mapNodeToMeeting } from '../utils'
import { usePrimaryCalendlyMeetingDetail } from './use_primary_calendly_meeting_detail'

export const MeetingDetailContext = createContext({})

/**
 * Provider for Meetings detail. Aligned with realtime meetings: runs REST getMeeting
 * in the provider and merges cache + REST so full details (e.g. location for INBOUND_CALL)
 * are available. Passes merged data as preloadedMeetingData to useActiveMeeting.
 */
export const MeetingDetailProvider = ({ eventId, shareId, children }) => {
  const shouldUseSupergraphMeetingDetail = useFlag('sa_meetings_page_with_supergraph', false)
  const hasMsTeamsFlag = useFlag('ms_teams_notetaker', false)
  const selectedView = useSelectedView()
  const { scopeType, scopeId } = useScope()
  const timezone = useSelector(timezoneSelector)
  const { preload: preloadParam } = useQueryParams()
  const isRealtimeFeatureEnabled = useNewCode('meetings_recaps_realtime_feature', false)?.enabled
  const allowPreload = Boolean(isRealtimeFeatureEnabled && (preloadParam === 'true' || preloadParam === true))
  const hasRealtimeFeaturesEnabled = useMeetingsRealtimeFeaturesEnabled()
  const isSSEConnectedFromHook = useSSEConnectedForPolling()
  // On realtime features disabled, ignore global SSE state so detail polling stays disabled without leaking realtime state.
  const isSSEConnected = hasRealtimeFeaturesEnabled ? isSSEConnectedFromHook : false

  const [triggerGraphQLFallback, {
    data: graphqlFallbackData,
    isLoading: graphqlFallbackLoading,
    isUninitialized: graphqlFallbackUninitialized,
  }] = useLazyGQLGetMeetingQuery()

  // Get meeting from RTK Query cache for instant display when opening from list
  const shouldUseCache = eventId && !shareId
  const cacheMeetingData = useMeetingFromCache(
    shouldUseCache ? eventId : null,
    mapNodeToMeeting,
    { scopeType, scopeId },
  )

  // Skip REST getMeeting for non-Calendly (external) events; scheduled_events API is for Calendly events only
  const isCalendlyMeeting = useMemo(() => {
    if (!cacheMeetingData) return true // No cache yet: allow REST call to determine
    return !cacheMeetingData.kind || cacheMeetingData.kind === 'CALENDLY'
  }, [cacheMeetingData])

  // External (non-Calendly) from cache only — used for polling decisions. REST is skipped for external.
  const isExternalEventFromCache = useMemo(
    () => Boolean(cacheMeetingData?.kind && cacheMeetingData.kind !== 'CALENDLY'),
    [cacheMeetingData]
  )

  // GraphQL fallback meeting (lazy) — used to derive external status for deep-linked external meetings.
  const graphqlFallbackMeeting = useMemo(() => {
    if (!graphqlFallbackData?.uuid || graphqlFallbackData.uuid !== eventId) return null
    return mapNodeToMeeting(graphqlFallbackData, timezone || 'UTC')
  }, [graphqlFallbackData, eventId, timezone])

  // External for polling: cache OR GraphQL fallback. Ensures 5s polling starts after fallback loads for deep-linked external meetings.
  const isExternalEventForPolling = useMemo(
    () => isExternalEventFromCache || Boolean(graphqlFallbackMeeting?.kind && graphqlFallbackMeeting.kind !== 'CALENDLY'),
    [isExternalEventFromCache, graphqlFallbackMeeting]
  )

  const {
    detailPollingConfig,
    shouldPollGraphQLForExternal,
    graphQLPollingQueryArgs,
  } = useLiveMeetingDetailPolling({
    enabled: hasRealtimeFeaturesEnabled,
    isSSEConnected, // false when realtime features disabled
    eventId,
    shareId,
    isExternalEvent: isExternalEventForPolling,
    selectedView,
  })

  const shouldFetchMeetingDetailInProvider = eventId && !shareId && selectedView !== 'pending' && scopeType && isCalendlyMeeting

  const {
    primaryMeetingData,
    primaryApiError,
    primaryApiSuccess,
    primaryApiErrorPayload,
  } = usePrimaryCalendlyMeetingDetail({
    shouldUseSupergraphMeetingDetail,
    shouldFetchMeetingDetailInProvider,
    eventId,
    selectedView,
    scopeType,
    scopeId,
    isCalendlyMeeting,
    detailPollingConfig,
    cacheMeetingData,
    timezone,
    hasMsTeamsFlag,
  })

  // Include graphqlFallbackMeeting so deep-linked external meetings (no cache/REST) get isExternalEvent true
  // and the merge logic below incorporates graphQLPolledMeeting into mergedMeetingData.
  const isExternalEvent = useMemo(() => {
    const meeting = cacheMeetingData || primaryMeetingData || graphqlFallbackMeeting
    return meeting && meeting.kind && meeting.kind !== 'CALENDLY'
  }, [cacheMeetingData, primaryMeetingData, graphqlFallbackMeeting])

  const { data: graphQLPollingData } = useGQLGetMeetingQuery(graphQLPollingQueryArgs, {
    ...detailPollingConfig,
    skip: !shouldPollGraphQLForExternal || !Object.keys(graphQLPollingQueryArgs).length,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
  })

  const graphQLPolledMeeting = useMemo(() => {
    if (!graphQLPollingData?.uuid || graphQLPollingData.uuid !== eventId) return null
    return mapNodeToMeeting(graphQLPollingData, timezone || 'UTC')
  }, [graphQLPollingData, eventId, timezone])

  // Merge cache + REST + GraphQL polling (for external) + GraphQL fallback so preloaded has full details
  const mergedMeetingData = useMemo(() => {
    if (isExternalEvent && graphQLPolledMeeting) {
      const base = defensiveMergeMeeting(
        primaryMeetingData || cacheMeetingData,
        graphQLPolledMeeting,
        { preserveMeetingIntelligence: false }
      )
      return defensiveMergeMeeting(base, graphqlFallbackMeeting, { preserveMeetingIntelligence: false })
    }
    const cachePlusPrimaryApi = defensiveMergeMeeting(cacheMeetingData, primaryMeetingData, { preserveMeetingIntelligence: false })
    return defensiveMergeMeeting(cachePlusPrimaryApi, graphqlFallbackMeeting, { preserveMeetingIntelligence: false })
  }, [
    isExternalEvent,
    graphQLPolledMeeting,
    cacheMeetingData,
    primaryMeetingData,
    graphqlFallbackMeeting,
  ])

  const preloadedDataForActiveMeeting = useMemo(() => {
    if (mergedMeetingData && mergedMeetingData.uuid === eventId) return mergedMeetingData
    if (cacheMeetingData && cacheMeetingData.uuid === eventId) return cacheMeetingData
    if (graphQLPolledMeeting && graphQLPolledMeeting.uuid === eventId) return graphQLPolledMeeting
    if (graphqlFallbackMeeting && graphqlFallbackMeeting.uuid === eventId) return graphqlFallbackMeeting
    return null
  }, [mergedMeetingData, cacheMeetingData, graphQLPolledMeeting, graphqlFallbackMeeting, eventId])

  const { activeMeeting, clearActiveMeeting, updateActiveMeeting, isLoading } = useActiveMeeting({
    selectedView,
    scopeType,
    scopeId,
    eventId,
    shareId,
    preloadedMeetingData: preloadedDataForActiveMeeting,
    // Avoid duplicate getMeeting: when we have eventId (no shareId), provider fetches once scope is ready;
    // don't trigger lazy fetch in useActiveMeeting so we don't get duplicate/pending calls on refresh (scope may be delayed).
    skipLazyFetchWhenProviderFetches: Boolean(eventId && !shareId),
    supergraphMeetingDetailEnabled: shouldUseSupergraphMeetingDetail,
    // Dependency injection for the consolidated hook
    useLazyGetMeetingQuery,
    useLazyGetMeetingFromSearchQuery,
    isPendingEvent,
    mapNodeToMeeting,
  })

  const { close } = useRightRail()
  const handled404ForEventRef = useRef(null)
  const clearedAfterGraphQLFallbackRef = useRef(null)

  // Reset 404/clear refs when eventId changes so each meeting gets a fresh 404 flow
  useEffect(() => {
    handled404ForEventRef.current = null
    clearedAfterGraphQLFallbackRef.current = null
  }, [eventId])

  // Detect 404 from the selected primary API.
  const isPrimaryApi404 = useMemo(() => {
    if (!primaryApiError || !primaryApiErrorPayload) return false
    const s = primaryApiErrorPayload.status
      ?? primaryApiErrorPayload.originalStatus
      ?? primaryApiErrorPayload.data?.status
      ?? primaryApiErrorPayload.data?.originalStatus
    if (s != null && Number(s) === 404) return true
    if (primaryApiErrorPayload?.type === 'missing') return true
    return false
  }, [primaryApiError, primaryApiErrorPayload])

  const isFullDataReady = useMemo(() => {
    if (allowPreload) return true
    if (!shouldFetchMeetingDetailInProvider) return !!activeMeeting

    const primarySuccessWithData = primaryApiSuccess && primaryMeetingData?.uuid === eventId
    const primary404AndGraphQLDone = isPrimaryApi404 && !graphqlFallbackUninitialized && !graphqlFallbackLoading

    // getMeetingFromSupergraph: transformResponse null → isSuccess with no data (no RTK error / HTTP 404).
    // Mirror the fallback effect: once lazy GraphQL has settled, stop blocking the rail (clear flow runs next).
    const primarySuccessNoMeeting = primaryApiSuccess
      && !primaryMeetingData?.uuid
      && !cacheMeetingData?.uuid
    const primaryNoMeetingAndGraphQLSettled = primarySuccessNoMeeting
      && !graphqlFallbackUninitialized
      && !graphqlFallbackLoading

    // List cache can supply the row while primary detail is empty (e.g. Supergraph miss).
    const primarySettledWithCacheHit = primaryApiSuccess && cacheMeetingData?.uuid === eventId

    return (
      primarySuccessWithData
      || primary404AndGraphQLDone
      || primaryNoMeetingAndGraphQLSettled
      || primarySettledWithCacheHit
    )
  }, [
    allowPreload,
    shouldFetchMeetingDetailInProvider,
    activeMeeting,
    primaryApiSuccess,
    primaryMeetingData,
    eventId,
    isPrimaryApi404,
    graphqlFallbackUninitialized,
    graphqlFallbackLoading,
    cacheMeetingData,
  ])

  // When primary API returns 404 or succeeds with no data, trigger GraphQL fallback.
  // GraphQL can return meetings (incl. external events) that the primary API may not have due to period/scope.
  // Without this, meetings not in the list (e.g. deep link from past/future) stay stuck in loading.
  useEffect(() => {
    if (!shouldFetchMeetingDetailInProvider || !eventId) return
    if (handled404ForEventRef.current === eventId) return

    const primary404 = isPrimaryApi404
    const primarySuccessNoData = primaryApiSuccess && !primaryMeetingData?.uuid && !cacheMeetingData?.uuid
    if (!primary404 && !primarySuccessNoData) return

    handled404ForEventRef.current = eventId
    triggerGraphQLFallback({ uuid: eventId })
  }, [
    shouldFetchMeetingDetailInProvider,
    isPrimaryApi404,
    primaryApiSuccess,
    primaryMeetingData,
    cacheMeetingData,
    eventId,
    triggerGraphQLFallback,
  ])

  // When GraphQL fallback has resolved and we have no meeting data, clear selection and close rail.
  // Only run after we've triggered the fallback (graphqlFallbackUninitialized false) and loading has finished.
  // Never clear when we have meeting data (REST or cache succeeded) so valid meetings don't close.
  useEffect(() => {
    if (!eventId || handled404ForEventRef.current !== eventId) return
    if (graphqlFallbackUninitialized || graphqlFallbackLoading) return
    if (graphqlFallbackData?.uuid === eventId) return
    if (preloadedDataForActiveMeeting?.uuid === eventId) return
    if (clearedAfterGraphQLFallbackRef.current === eventId) return
    clearedAfterGraphQLFallbackRef.current = eventId
    clearActiveMeeting()
    close()
  }, [
    eventId,
    graphqlFallbackUninitialized,
    graphqlFallbackLoading,
    graphqlFallbackData,
    preloadedDataForActiveMeeting,
    clearActiveMeeting,
    close,
  ])

  const context = useMemo(() => {
    const handleClose = () => {
      clearActiveMeeting() // clears URL params and selected meeting state
      close()
    }

    const showFullLoader = !allowPreload && !isFullDataReady
    return {
      selectedEvent: showFullLoader ? null : activeMeeting,
      handleClose,
      updateActiveMeeting,
      isLoading: showFullLoader ? true : isLoading,
    }
  }, [activeMeeting, clearActiveMeeting, updateActiveMeeting, isLoading, close, allowPreload, isFullDataReady])

  return (
    <MeetingDetailContext.Provider value={context}>
      {children}
    </MeetingDetailContext.Provider>
  )
}
